import React from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RestrictedTooltipHotspot } from '@atlassian/jira-issue-field-atlas-project-common/src/ui/restricted/tooltip-hotspot/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { AtlasProjectSmartLink } from '@atlassian/jira-issue-field-atlas-project-common/src/ui/atlas-project-smart-link/index.tsx';
import type { atlasProject_issueFieldAtlasProjectReadviewFull_AtlasProjectReadView$key as AtlasProjectFragment } from './__generated__/atlasProject_issueFieldAtlasProjectReadviewFull_AtlasProjectReadView.graphql';
import messages from './messages.tsx';
import type { AtlasProjectReadViewProps } from './types.tsx';

const styles = cssMap({
	container: {
		paddingTop: token('space.025'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.025'),
		paddingLeft: token('space.100'),
		height: '32px',
		width: '100%',
		textAlign: 'left',
		display: 'flex',
		alignItems: 'center',
	},
});

export const AtlasProjectEmptytView = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={styles.container}>
			<EmptyFieldText emptyText={formatMessage(messages.emptyFieldText)} />
		</Box>
	);
};

/**
 * The AtlasProjectReadView will show a read only view of AtlasProject field.
 * @param props [AtlasProjectReadViewProps](./types.tsx)
 */
export const AtlasProjectReadView = ({
	fragmentRef,
	onHandleNoAccess,
	showNoAccessTooltip,
}: AtlasProjectReadViewProps) => {
	const { formatMessage } = useIntl();
	const { project, isLinkEditable, issue } = useFragment<AtlasProjectFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment atlasProject_issueFieldAtlasProjectReadviewFull_AtlasProjectReadView on JiraTownsquareProjectField {
				isLinkEditable
				project {
					id @required(action: THROW)
					atlasProjectKey: key
					atlasProjectName: name
					privateProject
					hasPermission
					isWorkspaceActive
					iconName
				}
				issue {
					id @required(action: THROW)
				}
			}
		`,
		fragmentRef,
	);

	if (!project) {
		return <EmptyFieldText emptyText={formatMessage(messages.emptyFieldText)} />;
	}

	const content = (
		<AtlasProjectSmartLink
			projectAri={project.id}
			projectKey={project.atlasProjectKey}
			name={project.atlasProjectName}
			isPrivate={!!project.privateProject}
			isInherited={isLinkEditable === false}
			issueAri={issue?.id}
			initialHasNoAccess={project?.hasPermission === false || project?.isWorkspaceActive === false}
			onHandleNoAccess={onHandleNoAccess}
			iconName={project.iconName}
		/>
	);

	if (showNoAccessTooltip) {
		return (
			<RestrictedTooltipHotspot message={formatMessage(messages.noAccessTooltip)}>
				{content}
			</RestrictedTooltipHotspot>
		);
	}
	return content;
};
